'use client'
import { memo, useMemo, useRef, useCallback, useState, useEffect, type FC, type MouseEvent } from 'react'
import styled from 'styled-components'
import { useCart } from '@/context/project/CartContext'
import { LANG, ADD_TO_CART_TRANSLATION, REMOVE_FROM_CART_TRANSLATION, canAddToCart, canAddMoreQuantity } from '@/utils'
import { mq, vw } from '@/styles'
import type { TypeCmsVariant } from '@/schema'
import { QuantityInput } from './QuantityInput'
import { Button } from '@/components/base/Button'
import { AnimatePresence, motion } from 'motion/react'

type Props = {
	onClick?: (event: MouseEvent<HTMLButtonElement>) => void
	frozen?: boolean
	variant?: TypeCmsVariant
	showTextWhenNotInCart?: boolean
	horizontal?: boolean
	addPerBox?: boolean
	displayOnlyRemainingUnits?: boolean
}

const TIME_BATCHING_CLICKS_MS = 300

export const ButtonAddToCart: FC<Props> = memo(
	({ onClick, frozen = false, variant, showTextWhenNotInCart = false, horizontal = false, addPerBox = false, displayOnlyRemainingUnits = false }) => {
		const { addToCart, updateCartQuantity, removeFromCart, cartLines, isLoading } = useCart()

		// Net change tracking (for batching)
		const netChangeRef = useRef(0)
		const batchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

		const existingLine = useMemo(() => {
			const line = cartLines.find(line => line.variant.id === variant?.id)
			return line
		}, [cartLines, variant])

		const serverQuantity = useMemo(() => existingLine?.quantity || 0, [existingLine])

		// Local optimistic quantity (for display)
		const [optimisticQuantity, setOptimisticQuantity] = useState(serverQuantity)

		// Sync optimistic with server when cart updates
		useEffect(() => {
			queueMicrotask(() => setOptimisticQuantity(serverQuantity))
			netChangeRef.current = 0 // Reset net change when server updates
		}, [serverQuantity])

		const qtyPerBox = variant?.qtyPerBox || 1
		const minSaleUnits = variant?.minSaleUnits || 1
		const increment = addPerBox ? qtyPerBox : minSaleUnits

		const willDelete = useMemo(() => {
			if (!existingLine) return false
			if (addPerBox) {
				return optimisticQuantity - qtyPerBox <= 0
			}
			return optimisticQuantity - increment <= 0
		}, [existingLine, increment, addPerBox, optimisticQuantity, qtyPerBox])

		const validationResult = useMemo(() => {
			if (!variant) return { canAdd: false, reason: 'No variant' }
			return canAddToCart({ variant, cartLines, currentQuantity: optimisticQuantity, quantityToAdd: increment })
		}, [variant, cartLines, optimisticQuantity, increment])

		const canAddMore = useMemo(() => {
			if (!variant) return false
			if (variant?.product?.inventoryTracking === false) return true
			if (optimisticQuantity === 0) return validationResult.canAdd
			return canAddMoreQuantity(variant, optimisticQuantity, increment)
		}, [variant, optimisticQuantity, increment, validationResult.canAdd])

		const isDisabled = !validationResult.canAdd || (!canAddMore && optimisticQuantity > 0)

		// Execute batched changes
		const executeBatchedChange = useCallback(() => {
			if (!variant || netChangeRef.current === 0) {
				netChangeRef.current = 0
				return
			}

			const netChange = netChangeRef.current
			netChangeRef.current = 0 // Reset

			const newQuantity = serverQuantity + netChange

			if (newQuantity <= 0) {
				removeFromCart(variant)
			} else if (serverQuantity === 0 && newQuantity > 0) {
				console.log('addToCart', variant, newQuantity)
				addToCart(variant, newQuantity)
			} else {
				updateCartQuantity(variant, newQuantity)
			}
		}, [variant, serverQuantity, addToCart, updateCartQuantity, removeFromCart])

		// Unified handler for both add and remove
		const handleQuantityChange = useCallback((delta: number) => {
      if (!variant) return

      // Update net change (for batching)
      netChangeRef.current += delta

      // Update optimistic quantity (for display) - TRIGGERS RE-RENDER
      setOptimisticQuantity(prev => Math.max(0, prev + delta))

      // Clear existing timeout
      if (batchTimeoutRef.current) clearTimeout(batchTimeoutRef.current)

      // Set new timeout to batch changes
      batchTimeoutRef.current = setTimeout(() => {
        executeBatchedChange()
      }, TIME_BATCHING_CLICKS_MS) // batching window
    }, [variant, executeBatchedChange])

		// Add handler
		const handleAddToCart = useCallback(
			(event: MouseEvent<HTMLButtonElement>) => {
				event.preventDefault()
				event.stopPropagation()

				if (isDisabled || !variant) return

				onClick?.(event)
				handleQuantityChange(+increment) // Positive delta
			},
			[isDisabled, variant, onClick, increment, handleQuantityChange]
		)

		// Remove handler
		const handleRemoveFromCart = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      if (!variant) return

      handleQuantityChange(-increment) // Negative delta
    }, [variant, increment, handleQuantityChange])

		if (!variant) return <ButtonWrapper>...</ButtonWrapper>

		return (
			<ButtonWrapper>
				<CartControls $horizontal={horizontal}>
				  <AnimatePresence mode='wait'>
            {optimisticQuantity > 0 && <>
              <RemoveButton type='button' key={`remove-${variant?.id}`} $frozen={frozen} $isLoading={isLoading} onClick={handleRemoveFromCart} aria-label={REMOVE_FROM_CART_TRANSLATION[LANG]} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <IconWrapper $isDeleting={willDelete}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={willDelete ? '/images/svg/i--bin.svg' : '/images/svg/i--minus.svg'} alt={willDelete ? 'Delete' : 'Minus'} />
                </IconWrapper>
              </RemoveButton>
              <QuantityInput
                key={`quantity-${variant?.id}`}
                variant={variant}
                quantity={optimisticQuantity}
                frozen={frozen}
                horizontal={horizontal}
                addPerBox={addPerBox}
                displayOnlyRemainingUnits={displayOnlyRemainingUnits}
                isLoading={isLoading}
              />
            </>}
          </AnimatePresence>
          <AnimatePresence mode='wait'>
            {optimisticQuantity === 0 && showTextWhenNotInCart ?
              <AddTextButton type='button' bgColor='--color-text' $frozen={frozen} disabled={isDisabled} onClick={handleAddToCart}>
                {ADD_TO_CART_TRANSLATION[LANG]}
              </AddTextButton>
            :
              <AddButton type='button'$frozen={frozen} $disabled={isDisabled} $isLoading={isLoading} disabled={isDisabled} onClick={handleAddToCart} aria-label={ADD_TO_CART_TRANSLATION[LANG]}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src='/images/svg/i--plus.svg' alt='Plus icon' />
              </AddButton>
            }
				  </AnimatePresence>
				</CartControls>
			</ButtonWrapper>
		)
	}
)

const ButtonWrapper = styled.div`
	position: relative;
	margin-left: auto;
	margin-top: auto;
	padding: 8px;
`

const AddTextButton = styled(Button)<{ $frozen?: boolean }>`
	font-weight: 800;
	margin: 0;
	padding: ${vw(8, 'mobile')} ${vw(20, 'mobile')};

	${mq.greaterThan('nexus7')} {
		padding: ${vw(8, 'tablet')} ${vw(20, 'tablet')};
	}

	${mq.greaterThan('tablet')} {
		padding: ${vw(10, 'desktop')} ${vw(25, 'desktop')};
	}

	${mq.greaterThan('desktop')} {
		padding: 10px 25px;
	}

	${({ $frozen }) => $frozen && `
    background-color: var(--color-card-text--frozen);

    > div {
      background-color: var(--color-card-text--frozen);
    }
  `}
`

const CartControls = styled.div<{ $horizontal?: boolean }>`
	align-items: center;
	display: flex;
	flex-direction: row;
	gap: ${({ $horizontal }) => ($horizontal ? '8px' : '4px')};
	position: relative;
`

type AddButtonProps = { $frozen: boolean; $disabled?: boolean; $horizontal?: boolean; $isLoading?: boolean }

const AddButton = styled(motion.button)<AddButtonProps>`
	align-items: center;
	background-color: ${({ $disabled, $frozen }) => ($disabled ? `var(--color-card-button-bg--disabled${$frozen ? '--frozen' : ''})` : $frozen ? 'var(--color-card-text--frozen)' : 'var(--color-text)')};
	border-radius: 50%;
	color: var(--color-white);
	cursor: ${({ $disabled, $isLoading }) => ($disabled ? 'not-allowed' : $isLoading ? 'wait' : 'pointer')};
	display: flex;
	flex-shrink: 0;
	font-size: 24px;
	height: ${vw(44, 'mobile')};
	justify-content: center;
	${({ $horizontal }) => $horizontal && `margin-left: ${vw(-40, 'mobile')};`}
	opacity: ${({ $disabled, $isLoading }) => ($disabled ? 0.5 : $isLoading ? 0.7 : 1)};
	position: relative;
	width: ${vw(44, 'mobile')};
	transition: 500ms transform linear, opacity 300ms linear;
	z-index: 2;

	${mq.greaterThan('nexus7')} {
		height: ${vw(44, 'tablet')};
		width: ${vw(44, 'tablet')};
	}

	${mq.greaterThan('tablet')} {
		height: ${vw(44, 'desktop')};
		width: ${vw(44, 'desktop')};
	}

	${mq.greaterThan('desktop')} {
		height: 44px;
		width: 44px;
	}


	&:hover:not(:disabled) {
		transform: scale(1.1);

		&:before {
			transform: scale(1.1);
		}
	}

	&:disabled {
		background-color: ${({ $frozen }) => $frozen ? 'var(--color-card-button-bg--disabled--frozen)' : 'var(--color-card-button-bg--disabled)'};
		cursor: not-allowed;
		opacity: 1;
	}

  &:hover:not(:disabled) {
    img {
		  transform: scale(1.3);
    }
	}

	img {
		width: ${vw(13, 'mobile')};
		transition: 1200ms transform ease-in-out;

		${mq.greaterThan('nexus7')} {
			width: ${vw(13, 'tablet')};
		}

		${mq.greaterThan('tablet')} {
			width: ${vw(13, 'desktop')};
		}

		${mq.greaterThan('desktop')} {
			width: 13px;
		}
	}
`

const RemoveButton = styled(motion.button)<{ $frozen?: boolean; $isLoading?: boolean }>`
	align-items: center;
	background-color: ${({ $frozen }) => (!$frozen ? 'var(--color-text)' : 'var(--color-card-text--frozen)')};
	border-radius: 50%;
	color: var(--color-white);
	cursor: ${({ $isLoading }) => ($isLoading ? 'wait' : 'pointer')};
	opacity: ${({ $isLoading }) => ($isLoading ? 0.7 : 1)};
	display: flex;
	flex-shrink: 0;
	font-size: 24px;
	height: ${vw(44, 'mobile')};
	justify-content: center;
	position: relative;
	transition: transform 0.2s ease-in-out;
	width: ${vw(44, 'mobile')};
	z-index: 2;

	${mq.greaterThan('nexus7')} {
		height: ${vw(44, 'tablet')};
		width: ${vw(44, 'tablet')};
	}

	${mq.greaterThan('tablet')} {
		height: ${vw(44, 'desktop')};
		width: ${vw(44, 'desktop')};
	}

	${mq.greaterThan('desktop')} {
		height: 44px;
		width: 44px;
	}

	&:before {
		background-color: inherit;
		border-radius: 50%;
		content: '';
		height: 100%;
		left: 0;
		opacity: 1;
		position: absolute;
		top: 0;
		transition: transform 0.2s ease-in-out;
		width: 100%;
	}

	img {
		width: ${vw(13, 'mobile')};
		transition: transform 0.2s ease-in-out;

		${mq.greaterThan('nexus7')} {
			width: ${vw(13, 'tablet')};
		}

		${mq.greaterThan('tablet')} {
			width: ${vw(13, 'desktop')};
		}

		${mq.greaterThan('desktop')} {
			width: 13px;
		}
	}

	&:hover img {
		transform: scale(1.1);
	}
`

const IconWrapper = styled.div<{ $isDeleting: boolean }>`
	img {
		width: 13px;
		animation: ${({ $isDeleting }) => ($isDeleting ? 'rotateIn 0.3s ease-in-out' : 'rotateOut 0.3s ease-in-out')};
	}

	@keyframes rotateIn {
		from {
			opacity: 0;
			transform: rotate(-180deg);
		}
		to {
			opacity: 1;
			transform: rotate(0deg);
		}
	}

	@keyframes rotateOut {
		from {
			opacity: 1;
			transform: rotate(0deg);
		}
		to {
			opacity: 0;
			transform: rotate(180deg);
		}
	}
`
